import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { CreditCard, Layers3, Sparkles, Users } from 'lucide-react';
import type { ReactNode } from 'react';

export type BillingPlanSummaryModule = {
  id?: number | string;
  name: string;
  priceLabel: string;
};

type BillingPlanSummaryProps = {
  eyebrow?: string;
  planName: string;
  memberSince?: string;
  currentUsers: number;
  maximumUsers: number;
  usagePercentage: number;
  modules: BillingPlanSummaryModule[];
  totalLabel: string;
  features: string[];
  action?: ReactNode;
  className?: string;
};

export const BillingPlanSummary = ({
  eyebrow = 'Current plan',
  planName,
  memberSince,
  currentUsers,
  maximumUsers,
  usagePercentage,
  modules,
  totalLabel,
  features,
  action,
  className,
}: BillingPlanSummaryProps) => {
  return (
    <Card className={cn('overflow-hidden border-0 bg-card shadow-sm', className)}>
      <div className="h-2 bg-gradient-to-r from-primary via-chart-2 to-chart-4" />
      <CardHeader className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              {eyebrow}
            </p>
            <div className="space-y-1">
              <CardTitle className="text-3xl font-semibold tracking-tight sm:text-4xl">
                {planName}
              </CardTitle>
              {memberSince ? (
                <p className="text-sm text-muted-foreground">
                  Member since {memberSince}
                </p>
              ) : null}
            </div>
          </div>
          {action}
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl bg-background/80 p-4">
            <div className="mb-3 flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" />
              <span className="text-sm">User capacity</span>
            </div>
            <p className="text-2xl font-semibold">
              {currentUsers}
              <span className="ml-1 text-base font-normal text-muted-foreground">
                / {maximumUsers}
              </span>
            </p>
            <Progress value={usagePercentage} className="mt-3 h-2 rounded-full" />
          </div>

          <div className="rounded-2xl bg-background/80 p-4">
            <div className="mb-3 flex items-center gap-2 text-muted-foreground">
              <Layers3 className="h-4 w-4" />
              <span className="text-sm">Active modules</span>
            </div>
            <p className="text-2xl font-semibold">{modules.length}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {modules.slice(0, 3).map((module) => (
                <Badge key={module.id ?? module.name} variant="secondary" className="rounded-full">
                  {module.name}
                </Badge>
              ))}
              {modules.length > 3 ? (
                <Badge variant="outline" className="rounded-full">
                  +{modules.length - 3} more
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl bg-background/80 p-4">
            <div className="mb-3 flex items-center gap-2 text-muted-foreground">
              <CreditCard className="h-4 w-4" />
              <span className="text-sm">Monthly total</span>
            </div>
            <p className="text-2xl font-semibold">{totalLabel}</p>
            <p className="mt-3 text-sm text-muted-foreground">Across all selected modules</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 p-6 pt-0">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold">Module pricing</h3>
            <Badge variant="outline" className="rounded-full">
              {modules.length} modules
            </Badge>
          </div>
          <div className="overflow-hidden rounded-2xl bg-background/70">
            {modules.map((module, index) => (
              <div
                key={module.id ?? `${module.name}-${index}`}
                className={cn(
                  'grid grid-cols-[1fr_auto] gap-3 px-4 py-3 text-sm sm:text-base',
                  index !== modules.length - 1 && 'border-b border-border/60'
                )}
              >
                <span className="font-medium">{module.name}</span>
                <span className="text-right text-muted-foreground">{module.priceLabel}</span>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_auto] gap-3 border-t border-border/60 bg-muted/40 px-4 py-3 text-sm font-semibold sm:text-base">
              <span>Total</span>
              <span>{totalLabel}</span>
            </div>
          </div>
        </div>

        <Accordion type="single" collapsible>
          <AccordionItem
            value="features"
            className="rounded-2xl border border-border/60 bg-background/70 px-4"
          >
            <AccordionTrigger className="py-4 text-base font-medium no-underline hover:no-underline">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Features
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              {features.length ? (
                <div className="flex flex-wrap gap-2">
                  {features.map((feature) => (
                    <Badge key={feature} variant="secondary" className="rounded-full px-3 py-1">
                      {feature}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No features listed.</p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};
